import { createAsyncThunk } from '@reduxjs/toolkit'
import { addBimObjects, setBimObjects } from '../actions/bim'
import { BimCategory, BimObject, mockedBimObjects } from '../models/Bim'
import { isDevelop } from '../utils/Common'
import { BASE_URL } from '../config'
import { getIDToken } from '../utils/AuthUtil'
import { setBuildigBimObjects } from '../actions/construction'

type BimResponse = {
  hasMore: boolean
  nextOffset: number | null
  totalItems: number
  data: BimObject[]
}

const paginationCount = 100

export const fetchBimObjectsAsync = createAsyncThunk(
  'bim/fetchBimObjects',
  async ({ signal }: { signal?: AbortSignal }, { getState, dispatch }) => {
    if (isDevelop()) {
      dispatch(setBimObjects(mockedBimObjects))
      return
    }

    try {
      const categories = Object.values(BimCategory)
      for (const category of categories) {
        let hasMore: boolean = false
        let offset: number | null = 0

        try {
          do {
            const params = new URLSearchParams({
              limit: paginationCount.toString(),
            })
            if (offset) {
              params.set('offset', offset.toString())
            }
            params.set('category', category.toString().toLowerCase())

            const promise = isDevelop()
              ? fetch('', { signal })
              : fetch(`${BASE_URL}/api/bim/objects?${params.toString()}`, {
                  signal,
                  headers: {
                    'Access-Control-Allow-Origin': '*',
                    Authorization: `Bearer ${await getIDToken()}`,
                    'Content-Type': 'application/json',
                  },
                })

            const response = await promise
            if (!response.ok) {
              throw await response.json()
            }

            const responseJson: BimResponse = await response.json()
            hasMore = responseJson.hasMore
            offset = responseJson.nextOffset

            dispatch(addBimObjects(responseJson.data))
          } while (hasMore)
        } catch (error: any) {
          if (error.name !== 'AbortError') {
            console.error(
              `Error during fetching BIM objects for '${category}': `,
              error,
            )
          } else {
            throw error
          }
        }
      }
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error(`Error during fetching BIM objects: `, error)
      }
    }
  },
)

export const getBimObjectsAsync = createAsyncThunk(
  'bim/fetchBimObjects',
  async (
    { signal, bimObjectsIds }: { signal: AbortSignal; bimObjectsIds: string[] },
    { getState, dispatch },
  ) => {
    try {
      const promise = fetch(`${BASE_URL}/api/bim/objects`, {
        method: 'POST',
        signal,
        headers: {
          'Access-Control-Allow-Origin': '*',
          Authorization: `Bearer ${await getIDToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bimObjectsIds),
      })

      const response = await promise
      if (!response.ok) {
        throw await response.json()
      }

      const bimObjects: BimObject[] = await response.json()
      dispatch(setBuildigBimObjects(bimObjects))
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error(`Error during fetching BIM objects: `, error)
      }
    }
  },
)
